/**
 * Network connectivity check utilities
 * Helps detect and handle network connectivity issues that might affect Firebase operations
 */

// Event listener for online/offline status
let isOnline = navigator.onLine;
let onlineStatusChangeCallbacks: Function[] = [];

// Update online status and notify listeners
const updateOnlineStatus = () => {
  const wasOnline = isOnline;
  isOnline = navigator.onLine;
  
  // Only notify if the status actually changed
  if (wasOnline !== isOnline) {
    onlineStatusChangeCallbacks.forEach(callback => callback(isOnline));
  }
}

// Set up event listeners for online/offline events
window.addEventListener('online', updateOnlineStatus);
window.addEventListener('offline', updateOnlineStatus);

/**
 * Get the current online status
 */
export const getOnlineStatus = (): boolean => {
  return isOnline;
};

/**
 * Register a callback for online/offline status changes
 * @param callback Function to call when online status changes
 * @returns Function to unregister the callback
 */
export const onOnlineStatusChange = (callback: (isOnline: boolean) => void): () => void => {
  onlineStatusChangeCallbacks.push(callback);
  
  // Immediately call with current status
  callback(isOnline);
  
  // Return unsubscribe function
  return () => {
    onlineStatusChangeCallbacks = onlineStatusChangeCallbacks.filter(cb => cb !== callback);
  };
};

/**
 * Actively check if Firebase is reachable
 * This is more reliable than just checking navigator.onLine
 */
export const checkFirebaseConnectivity = async (): Promise<boolean> => {
  try {
    // First check if browser reports as online
    if (!navigator.onLine) {
      return false;
    }
    
    // Use a more reliable endpoint that's less likely to be blocked
    // This is the Firebase Auth status endpoint which is usually accessible
    const response = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${process.env.REACT_APP_FIREBASE_API_KEY || ''}`, {
      method: 'HEAD', // Just checking headers, not making an actual request
      cache: 'no-cache',
      mode: 'cors',
      // Add timeout to prevent hanging requests
      signal: AbortSignal.timeout(5000) // 5 second timeout
    });
    
    // Consider a 4xx response as "connected" since it means we reached Firebase
    // (it may return 400 because we're not sending proper data - that's fine)
    return response.status < 500;
  } catch (error) {
    console.warn('Firebase connectivity check failed:', error);
    // If it's an AbortError (timeout), consider it a connectivity issue
    if (error instanceof DOMException && error.name === 'AbortError') {
      console.warn('Firebase connectivity check timed out');
      return false;
    }
    // Be more lenient - return true if we can't definitively say it's disconnected
    // This helps prevent false negatives
    return true;
  }
};

/**
 * Display a network error message to the user
 * This can be used when a network error is detected
 */
export const showNetworkErrorMessage = (): void => {
  // Check if we already have a network alert shown
  if (document.getElementById('network-error-alert')) {
    return;
  }
  
  // Create a toast/alert element
  const alert = document.createElement('div');
  alert.id = 'network-error-alert';
  alert.style.position = 'fixed';
  alert.style.bottom = '20px';
  alert.style.right = '20px';
  alert.style.backgroundColor = '#f44336';
  alert.style.color = 'white';
  alert.style.padding = '16px';
  alert.style.borderRadius = '4px';
  alert.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
  alert.style.zIndex = '9999';
  alert.style.display = 'flex';
  alert.style.alignItems = 'center';
  alert.style.justifyContent = 'space-between';
  
  // Add message
  alert.innerHTML = `
    <div>
      <strong>Network Connection Error</strong><br>
      Please check your internet connection and try again.
    </div>
    <button style="background: transparent; border: none; color: white; margin-left: 16px; cursor: pointer; font-size: 20px;">×</button>
  `;
  
  // Add to DOM
  document.body.appendChild(alert);
  
  // Add close functionality
  const closeButton = alert.querySelector('button');
  if (closeButton) {
    closeButton.addEventListener('click', () => {
      document.body.removeChild(alert);
    });
  }
  
  // Auto-remove after 10 seconds
  setTimeout(() => {
    if (document.body.contains(alert)) {
      document.body.removeChild(alert);
    }
  }, 10000);
};

/**
 * Initialize network monitoring
 * This should be called once when the app starts
 */
export const initializeNetworkMonitoring = (): void => {
  // Monitor online/offline status changes
  onOnlineStatusChange((online) => {
    if (!online) {
      showNetworkErrorMessage();
    }
  });
  
  // Only check connectivity when the user reports seeing issues
  // Removing the automatic interval check to avoid excessive API calls
  /* 
  setInterval(async () => {
    const isConnected = await checkFirebaseConnectivity();
    if (!isConnected && navigator.onLine) {
      // If browser thinks we're online but Firebase is unreachable
      showNetworkErrorMessage();
    }
  }, 60000); // Check every minute
  */
};

// Add a global debug function that can be called from the browser console
export const debugFirebaseConnection = async (): Promise<void> => {
  console.log("🔍 Testing Firebase connectivity...");
  console.log("Browser online status:", navigator.onLine ? "ONLINE" : "OFFLINE");
  
  try {
    // Test direct connectivity to Firebase Authentication
    console.log("Testing Firebase Auth connectivity...");
    const authResponse = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${process.env.REACT_APP_FIREBASE_API_KEY || ''}`, {
      method: 'HEAD',
      cache: 'no-cache',
      mode: 'cors',
      signal: AbortSignal.timeout(5000) // 5 second timeout
    });
    console.log("Firebase Auth reachable:", authResponse.status < 500 ? "YES" : "NO", `(Status: ${authResponse.status})`);
    
    // Test direct connectivity to Firestore
    console.log("Testing Firestore connectivity...");
    const firestoreResponse = await fetch('https://firestore.googleapis.com/google.firestore.v1.Firestore/Listen/channel', {
      method: 'OPTIONS',
      cache: 'no-cache',
      signal: AbortSignal.timeout(5000) // 5 second timeout
    });
    console.log("Firestore reachable:", firestoreResponse.ok ? "YES" : "NO", `(Status: ${firestoreResponse.status})`);
    
    // Check if the API key might be restricted
    console.log("Checking API key restrictions...");
    try {
      // This fetch should fail for normal reasons (missing params), not due to API key restrictions
      const apiKeyCheck = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${process.env.REACT_APP_FIREBASE_API_KEY || ''}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Referer': window.location.origin
        },
        body: JSON.stringify({ returnSecureToken: true }),
        signal: AbortSignal.timeout(5000)
      });
      const response = await apiKeyCheck.json();
      console.log("API key check response:", response);
      // If we get an error about missing email/password, that's good - the API key is working
      if (response.error && response.error.message === 'MISSING_EMAIL') {
        console.log("✅ API key is valid and not restricted");
      } else if (response.error && response.error.message === 'API key not valid. Please pass a valid API key.') {
        console.log("❌ API key is invalid");
      } else if (response.error && response.error.message.includes('API key restriction')) {
        console.log("❌ API key has restrictions that prevent it from being used from this domain");
      }
    } catch (error) {
      console.log("API key check failed:", error);
    }
    
    console.log("All tests complete. If any test failed, try using a different network or browser.");
  } catch (error) {
    console.error("❌ Firebase connectivity test failed with error:", error);
    console.log("Recommendation: Try disabling browser extensions, using incognito mode, or trying a different network.");
  }
};

// Make it available in the window object for console debugging
if (typeof window !== 'undefined') {
  (window as any).testFirebaseConnection = debugFirebaseConnection;
}

const networkCheck = {
  getOnlineStatus,
  onOnlineStatusChange,
  checkFirebaseConnectivity,
  showNetworkErrorMessage,
  initializeNetworkMonitoring,
};
export default networkCheck; 